import { useState } from 'react';
import { Button, Spin, Tag, notification } from 'antd';
import CommonLayout from '../../../layouts/Common';
import { StyledButton } from '../../../components/Common/Button/style';
import { ColumnsType } from 'antd/es/table';
import { HeaderData, NameData, StatusWrapper } from './style';
import CommonTable from '../../../components/Common/Table';
import useGetAllProfessionals from '../../../hooks/professionals/useGetAllProfessionals';
import { ProfessionalType } from '../../../types';
import { copyToClipboard, formatDate } from '../../../helpers';
import ProfessionalUserForm from '../../../components/Forms/Admin/User/Professional';
import { useAdminSignInAsProfessional, useCreateProfessionalUser, useResendProfessionalEmailInvitation, useUpdateProfessionalUser } from '../../../hooks/admin/useAdminProfessional';
import { CheckCircleOutlined, LoginOutlined } from '@ant-design/icons';
import { theme } from '../../../styles/theme';
import { NotificationTitle } from '../../../components/Common/Notification/style';
import { useDispatch, useSelector } from 'react-redux';
import MaterialIcon from '../../../components/Icons/MaterialIcon';
import Cookies from 'js-cookie';
import { authActions } from '../../../redux/slices';
import { useNavigate } from 'react-router-dom';

const Professionals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { storeAuth } = authActions;
  const [api, contextHolder] = notification.useNotification();
  const professionals = useSelector((state: any) => state?.professionals?.professionals);
  const { create: resendInvitation, loading: resendInvitationLoading } = useResendProfessionalEmailInvitation();
  const { update: createProfessionalUser, loading: createLoading } = useCreateProfessionalUser();
  const { update: updateProfessionalUser, loading: updateLoading } = useUpdateProfessionalUser();
  const { signInAsProfessional, loading: signInLoading } = useAdminSignInAsProfessional();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { refetch: refetchProfessionals, loading, perPage, total } = useGetAllProfessionals(currentPage);
  const [openNewProfessional, setOpenNewProfessional] = useState<boolean>(false);
  const [openEditProfessional, setOpenEditProfessional] = useState<boolean>(false);
  const [editProfessionalValues, setEditProfessionalValues] = useState<any>({
    email: '',
    firstName: '',
    familyName: '',
    password: '',
    confirmPassword: '',
    language: '',
    timezone: '',
  });

  console.log(professionals);

  const columns: ColumnsType<ProfessionalType> = [
    {
      title: <HeaderData>Name</HeaderData>,
      render: (text, record) => (
        <NameData onClick={() => onEdit(record)}>{record.firstName} {record.familyName}</NameData>
      ),
    },
    {
      title: <HeaderData>Email</HeaderData>,
      render: (text, record) => (
        <span>{record.email}</span>
      ),
    },
    {
      title: <HeaderData>Type</HeaderData>,
      dataIndex: 'type',
      render: (text) => {
        let type = '';
        switch (text.replace('ProfessionalType', '')) {
          case 'QuestionnaireOnly':
            type = 'Questionnaire-only';
            break;
          case 'A':
            type = 'Type A';
            break;
          default:
            type = text.replace('ProfessionalType', '');
        }
        return (
          <span>{type}</span>
        )
      }
    },
    {
      title: <HeaderData>Code</HeaderData>,
      dataIndex: 'code',
    },
    {
      title: <HeaderData>Created</HeaderData>,
      dataIndex: 'createdAt',
      render: (text) => (
        <>
          {formatDate(text)}
        </>
      )
    },
    {
      title: <HeaderData>Status</HeaderData>,
      // dataIndex: 'twoFactorEnabled',
      render: (text, record) => (
        <>
          {record?.accountStatus === 'active' ?
            <Tag color="green">active</Tag>
            : (
              <StatusWrapper>
                {/* <Input
                  value={record.emailValidationUrl}
                  type="text"
                  readOnly
                  disabled
                /> */}
                <Button
                  onClick={() =>
                    copyToClipboard(record?.emailValidationUrl as string)
                  }
                  icon={<MaterialIcon icon="content_copy" className="material-icons-sharp" />}
                />
                <Button
                  loading={resendInvitationLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResendEmailInvitation(record.id);
                  }}
                  icon={<MaterialIcon icon="send" className="material-icons-sharp" />}
                />
              </StatusWrapper>
            )
          }
        </>
      ),
    },
    {
      title: <HeaderData>Login as</HeaderData>,
      dataIndex: 'action',
      render: (text, record) => (
        <StyledButton 
          loading={signInLoading} 
          onClick={(e) => {
            e.stopPropagation();
            onLoginAs(record);
          }} 
          icon={<LoginOutlined />} 
        />
      ),
    }
  ];

  const onEdit = (record: any) => {
    setEditProfessionalValues({
      ...record
    });
    setOpenEditProfessional(true);
  };

  const onLoginAs = async (record: any) => {
    try {
      const res = await signInAsProfessional(record.email);
      if (res?.token) {
        // Save current admin credentials before switching
        const currentToken = Cookies.get("auth_token");
        const currentUser = Cookies.get("user");
        
        if (currentToken) Cookies.set("admin_auth_token", currentToken, { expires: 1 });
        if (currentUser) Cookies.set("admin_user", currentUser, { expires: 1 });

        const user = { 
          ...record, 
          role: 'Professional', 
          isReadOnly: true 
        };

        Cookies.set("user", JSON.stringify(user), { expires: 1 });
        Cookies.set("auth_token", res.token, { expires: 1 });

        dispatch(
          storeAuth({
            token: res.token,
            user,
          })
        );

        navigate(`/p/${record.code}`);
      }
    } catch (error) {
      console.error('onLoginAs error', error);
      api.error({
        message: <NotificationTitle>Failed to login as professional</NotificationTitle>,
        placement: 'topRight',
      });
    }
  };

  const onSubmit = async (record: any) => {
    if (openEditProfessional) {
      const res = await updateProfessionalUser(record);
      if (res.success) {
        refetchProfessionals();
        api.success({
          message: <NotificationTitle>Successfully Updated Professional</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
        setOpenNewProfessional(false);
        setOpenEditProfessional(false);
      }
    } else {
      try {
        const res = await createProfessionalUser(record);
        if (res.success) {
          refetchProfessionals();
          api.success({
            message: <NotificationTitle>{res.message}</NotificationTitle>,
            placement: 'topRight',
            icon:
              <CheckCircleOutlined
                style={{
                  color: theme.color.primary,
                }}
              />,
            style: {
              background: `${theme.bg.secondary}`,
              borderRadius: 10,
              alignItems: 'center',
              display: 'flex'
            }
          });
          setOpenNewProfessional(false);
        }
      } catch (error: any) {
        console.error('onSubmit createProfessionalUser error', error)
      } finally {
        refetchProfessionals()
      }
    }
  };

  const onDelete = () => {
    refetchProfessionals();
    api.success({
      message: <NotificationTitle>Successfully Deleted Professional</NotificationTitle>,
      placement: 'topRight',
      icon:
        <CheckCircleOutlined
          style={{
            color: theme.color.primary,
          }}
        />,
      style: {
        background: `${theme.bg.secondary}`,
        borderRadius: 10,
        alignItems: 'center',
        display: 'flex'
      }
    });
    setOpenEditProfessional(false);
  };

  const handleBack = () => {
    setEditProfessionalValues({
      email: '',
      firstName: '',
      familyName: '',
      password: '',
      confirmPassword: '',
      language: '',
      timezone: '',
    });
    setOpenNewProfessional(false);
    setOpenEditProfessional(false);
  };

  const onResendEmailInvitation = async (professionalId: string) => {
    try {
      const res = await resendInvitation({ professionalId });
      if (res.success) {
        api.success({
          message: <NotificationTitle>{res.message}</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
      }
    } catch (error: any) {
      console.error('onResendEmailInvitation error', error);
    }
  };

  return (
    <CommonLayout title="Professionals" width="1000px">
      {contextHolder}
      {loading ? <Spin size="large" /> : (
        <CommonTable
          showNewModal={openNewProfessional}
          onNewData={setOpenNewProfessional}
          showEditModal={openEditProfessional}
          onEditData={setOpenEditProfessional}
          handleBack={handleBack}
          modalForm={
            <ProfessionalUserForm
              defaultValues={editProfessionalValues}
              onBack={handleBack}
              onSubmit={onSubmit}
              onDelete={onDelete}
              loading={createLoading || updateLoading || loading}
              isEdit={openEditProfessional}
              onReset={openNewProfessional}
            />
          }
          columns={columns}
          dataSource={professionals ?? []}
          page={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          totalData={total}
          tableType="Professional"
          total={`${professionals?.length} professionals`}
        />
      )}
    </CommonLayout>
  )
};

export default Professionals;